import React from "react";
import { buildCollection, buildProperty } from "@firecms/core";
import {
    platformEnumValues,
    rateDialogActionEnumValues,
} from "./shared/enums";
import { MissingOrNumberPreview } from "./shared/missing_number";
import {
    DeviceTrialCount,
    UserHasFeedbackLabel,
    UserRelatedView,
} from "./shared/user_related_views";
import { userHistoryCollection } from "./user_history";
import { userQuizzesCollection } from "./user_quizzes";

/**
 * Uygulama kullanıcısı (Firestore: users/{uid}).
 * Alanlar Flutter UserModel + webhook isPro ile uyumludur.
 */
export type AppUser = {
    uid?: string;
    deviceId: string;
    /** İlk kayıtta yazılır; anasayfa filtreleri bu alanı kullanır. */
    platform?: keyof typeof platformEnumValues;
    createdAt: Date;
    preferredName?: string;
    lastUsageDate?: Date;
    dailyTotalLimitLeft?: number;
    dailyImageLimitLeft?: number;
    dailyPdfLimitLeft?: number;
    dailyYoutubeLimitLeft?: number;
    dailyQuizLimitLeft?: number;
    dailyVideoLimitLeft?: number;
    /** Webhook yazar; yoksa ücretsiz. */
    isPro?: boolean;
    proUpdatedAt?: Date;
    rateDialogAction?: keyof typeof rateDialogActionEnumValues;
    rateDialogAt?: Date;
    rateDialogAppVersion?: string;
};

const readOnlyLimit = (name: string) =>
    buildProperty({
        name,
        dataType: "number",
        readOnly: true,
        Preview: MissingOrNumberPreview,
    });

/**
 * Kullanıcılar — create / edit / delete kapalı.
 * Sohbet ve quiz gerçek alt koleksiyon; deneme + feedback mevcut kök belgelerden okunur.
 */
export const usersCollection = buildCollection<AppUser>({
    id: "users",
    name: "Kullanıcılar",
    singularName: "Kullanıcı",
    path: "users",
    group: "Kullanıcılar",
    icon: "people",
    description: "Anonim uygulama kullanıcıları (salt okunur)",
    titleProperty: "preferredName",
    initialSort: ["createdAt", "desc"],
    permissions: {
        read: true,
        create: false,
        edit: false,
        delete: false,
    },
    propertiesOrder: [
        "preferredName",
        "isPro",
        "deviceId",
        "platform",
        "rateDialogAction",
        "rateDialogAt",
        "rateDialogAppVersion",
        "createdAt",
        "lastUsageDate",
        "has_feedback",
        "free_trial_left",
    ],
    additionalFields: [
        {
            key: "has_feedback",
            name: "Geri bildirim",
            Builder: ({ entity }) => (
                <UserHasFeedbackLabel userId={entity.id} />
            ),
        },
        {
            key: "free_trial_left",
            name: "Kalan deneme",
            Builder: ({ entity }) => (
                <DeviceTrialCount deviceId={entity.values.deviceId} />
            ),
        },
    ],
    subcollections: [userHistoryCollection, userQuizzesCollection],
    entityViews: [
        {
            key: "related",
            name: "Deneme ve geri bildirim",
            Builder: UserRelatedView,
        },
    ],
    properties: {
        preferredName: buildProperty({
            name: "Tercih edilen isim",
            dataType: "string",
            readOnly: true,
        }),
        uid: buildProperty({
            name: "Kullanıcı ID",
            dataType: "string",
            readOnly: true,
        }),
        deviceId: buildProperty({
            name: "Cihaz ID",
            dataType: "string",
            readOnly: true,
        }),
        platform: buildProperty({
            name: "Platform",
            dataType: "string",
            enumValues: platformEnumValues,
            readOnly: true,
        }),
        rateDialogAction: buildProperty({
            name: "Puanlama seçimi",
            dataType: "string",
            enumValues: rateDialogActionEnumValues,
            readOnly: true,
        }),
        rateDialogAt: buildProperty({
            name: "Puanlama zamanı",
            dataType: "date",
            mode: "date_time",
            readOnly: true,
        }),
        rateDialogAppVersion: buildProperty({
            name: "Puanlama sürümü",
            dataType: "string",
            readOnly: true,
        }),
        createdAt: buildProperty({
            name: "Oluşturulma",
            dataType: "date",
            mode: "date_time",
            readOnly: true,
        }),
        lastUsageDate: buildProperty({
            name: "Son kullanım",
            dataType: "date",
            mode: "date_time",
            readOnly: true,
        }),
        isPro: buildProperty({
            name: "Premium",
            dataType: "boolean",
            readOnly: true,
            Preview: ({ value }) => (value === true ? "Evet" : "Hayır"),
        }),
        proUpdatedAt: buildProperty({
            name: "Premium güncelleme",
            dataType: "date",
            mode: "date_time",
            readOnly: true,
        }),
        dailyTotalLimitLeft: readOnlyLimit("Kalan günlük toplam"),
        dailyImageLimitLeft: readOnlyLimit("Kalan günlük görsel"),
        dailyPdfLimitLeft: readOnlyLimit("Kalan günlük PDF"),
        dailyYoutubeLimitLeft: readOnlyLimit("Kalan günlük YouTube"),
        dailyQuizLimitLeft: readOnlyLimit("Kalan günlük quiz"),
        dailyVideoLimitLeft: readOnlyLimit("Kalan günlük video"),
    },
});
